import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  getAimSnapshot,
  getAimSnapshotTrend,
  getAimMentionRateTrend,
  getAimRpc,
  getAimRun,
  getAimRuns,
  getAimScorecard,
  getAimHeatmap,
  getAimHeatmapCell,
  getAimKpis,
  getAimSearchLift,
  getAimSentiment,
  getAimProviderCategories,
  getAimProviderFacts,
  getAimProviderCitations,
  getAimProviderCoverage,
  getAimCompetitorThreat,
  getAimShareOfVoice,
  getAimCompetitorDetail,
  getAimCitationSources,
  getAimAmenityGap,
  getAimKnowledgeRecencyTimeline,
  getAimAioFunnel,
  getAimOrganicRankingsDetail,
  getAimHallucinations,
} from '../../../api/auth';
import { normalizeAimRun } from './runLabel';
import { unwrapSnapshot } from './snapshotShape';

/**
 * Run an async read and expose its state.
 *
 * `settled` is true once the latest request has finished, with data or with an error, and false
 * from the moment one starts (and before the first). It exists because `{data: null}` cannot say
 * whether a read is still on its way or came back empty: a SQL NULL result resolves to null.
 */
function useAsync(fn, deps, enabled = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [settled, setSettled] = useState(false);
  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setSettled(false);
    setError(null);
    try {
      const result = await fn();
      setData(result);
    } catch (e) {
      setError(e?.message || String(e));
      setData(null);
    } finally {
      setLoading(false);
      setSettled(true);
    }
  }, [fn, enabled]);

  useEffect(() => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      setSettled(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSettled(false);
    setError(null);
    Promise.resolve(fn())
      .then((result) => { if (!cancelled) setData(result); })
      .catch((e) => { if (!cancelled) { setError(e?.message || String(e)); setData(null); } })
      .finally(() => { if (!cancelled) { setLoading(false); setSettled(true); } });
    return () => { cancelled = true; };
  }, deps); // eslint-disable-line

  return { data, loading, error, refresh, enabled, settled };
}

export function useAimSnapshot(clientId, metricCode, dimensionKey, runId) {
  return useAsync(
    async () => {
      if (!clientId || !metricCode) return null;
      const resp = await getAimSnapshot(clientId, metricCode, dimensionKey, runId);
      // fn_get_aim_snapshot returns a 1-row array (RETURNS TABLE); unwrap it so
      // consumers can read snap.value_numeric / snap.value_breakdown directly.
      return unwrapSnapshot(resp?.data);
    },
    [clientId, metricCode, dimensionKey, runId],
    Boolean(clientId && metricCode),
  );
}

export function useAimSnapshotTrend(clientId, metricCode, startDate, endDate, dimensionKey) {
  return useAsync(
    async () => {
      if (!clientId || !metricCode) return [];
      const resp = await getAimSnapshotTrend(clientId, metricCode, startDate, endDate, dimensionKey);
      return resp?.data ?? [];
    },
    [clientId, metricCode, startDate, endDate, dimensionKey],
    Boolean(clientId && metricCode),
  );
}

export function useAimMentionRateTrend(clientId, limitRuns = 30) {
  return useAsync(
    async () => {
      if (!clientId) return [];
      const resp = await getAimMentionRateTrend(clientId, limitRuns);
      return resp?.data ?? [];
    },
    [clientId, limitRuns],
    Boolean(clientId),
  );
}

export function useAimRpc(clientId, rpcName, extra = {}) {
  const extraKey = JSON.stringify(extra || {});
  return useAsync(
    async () => {
      if (!clientId || !rpcName) return null;
      const resp = await getAimRpc(rpcName, { clientId, ...extra });
      return resp?.data ?? null;
    },
    [clientId, rpcName, extraKey],
    Boolean(clientId && rpcName),
  );
}

export function useAimRuns(clientId, limit = 20) {
  return useAsync(
    async () => {
      if (!clientId) return [];
      const resp = await getAimRuns(clientId, limit);
      return resp?.data ?? [];
    },
    [clientId, limit],
    Boolean(clientId),
  );
}

export function useAimRun(clientId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return null;
      const resp = await getAimRun(clientId, runId);
      return normalizeAimRun(resp?.data ?? null);
    },
    [clientId, runId],
    Boolean(clientId && runId),
  );
}

export function useAimScorecard(clientId, runId, compareRunId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return [];
      const resp = await getAimScorecard(clientId, runId, compareRunId);
      return resp?.data ?? [];
    },
    [clientId, runId, compareRunId],
    Boolean(clientId && runId),
  );
}

export function useAimHeatmap(clientId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return null;
      const resp = await getAimHeatmap(clientId, runId);
      return resp?.data ?? null;
    },
    [clientId, runId],
    Boolean(clientId && runId),
  );
}

/**
 * The responses behind one heatmap cell. Waits for the run, category and provider.
 *
 * @param {string} clientId
 * @param {{category: string, provider: string, runId: string, limit?: number}} cell
 */
export function useAimHeatmapCell(clientId, { category, provider, runId, limit } = {}) {
  return useAsync(
    async () => {
      const resp = await getAimHeatmapCell(clientId, { category, provider, runId, limit });
      return resp?.data ?? null;
    },
    [clientId, category, provider, runId, limit],
    Boolean(clientId && runId && category && provider),
  );
}

export function useAimKpis(clientId, runId, compareRunId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return null;
      const resp = await getAimKpis(clientId, runId, compareRunId);
      return resp?.data ?? null;
    },
    [clientId, runId, compareRunId],
    Boolean(clientId && runId),
  );
}

export function useAimHallucinations(clientId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return [];
      const resp = await getAimHallucinations(clientId, { runId });
      return Array.isArray(resp?.data) ? resp.data : [];
    },
    [clientId, runId],
    Boolean(clientId && runId),
  );
}

export function useAimSearchLift(clientId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return [];
      const resp = await getAimSearchLift(clientId, runId);
      return resp?.data ?? [];
    },
    [clientId, runId],
    Boolean(clientId && runId),
  );
}

export function useAimSentiment(clientId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return [];
      const resp = await getAimSentiment(clientId, runId);
      return resp?.data ?? [];
    },
    [clientId, runId],
    Boolean(clientId && runId),
  );
}

export function useAimProviderCategories(clientId, providerId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !providerId || !runId) return [];
      const resp = await getAimProviderCategories(clientId, providerId, runId);
      return resp?.data ?? [];
    },
    [clientId, providerId, runId],
    Boolean(clientId && providerId && runId),
  );
}

export function useAimProviderFacts(clientId, providerId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !providerId || !runId) return [];
      const resp = await getAimProviderFacts(clientId, providerId, runId);
      return resp?.data ?? [];
    },
    [clientId, providerId, runId],
    Boolean(clientId && providerId && runId),
  );
}

export function useAimProviderCitations(clientId, providerId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !providerId || !runId) return [];
      const resp = await getAimProviderCitations(clientId, providerId, runId);
      return resp?.data ?? [];
    },
    [clientId, providerId, runId],
    Boolean(clientId && providerId && runId),
  );
}

export function useAimProviderCoverage(clientId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return [];
      const resp = await getAimProviderCoverage(clientId, runId);
      return resp?.data ?? [];
    },
    [clientId, runId],
    Boolean(clientId && runId),
  );
}

/**
 * Competitor threat rows and their window. Waits for a run: without one the RPC would fetch its
 * default window and then refetch once the run resolves, flashing a window that doesn't match the
 * drawer's.
 */
export function useAimCompetitorThreat(clientId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return null;
      const resp = await getAimCompetitorThreat(clientId, runId);
      return resp?.data ?? null;
    },
    [clientId, runId],
    Boolean(clientId && runId),
  );
}

export function useAimShareOfVoice(clientId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return [];
      const resp = await getAimShareOfVoice(clientId, runId);
      return resp?.data ?? [];
    },
    [clientId, runId],
    Boolean(clientId && runId),
  );
}

export function useAimCompetitorDetail(clientId, runId, competitorName) {
  return useAsync(
    async () => {
      if (!clientId || !runId || !competitorName) return [];
      const resp = await getAimCompetitorDetail(clientId, runId, competitorName);
      return resp?.data ?? [];
    },
    [clientId, runId, competitorName],
    Boolean(clientId && runId && competitorName),
  );
}

export function useAimCitationSources(clientId, runId, limit) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return [];
      const resp = await getAimCitationSources(clientId, runId, { limit });
      return resp?.data ?? [];
    },
    [clientId, runId, limit],
    Boolean(clientId && runId),
  );
}

export function useAimAmenityGap(clientId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return null;
      const resp = await getAimAmenityGap(clientId, runId);
      return resp?.data ?? null;
    },
    [clientId, runId],
    Boolean(clientId && runId),
  );
}

export function useAimKnowledgeRecencyTimeline(clientId) {
  return useAsync(
    async () => {
      if (!clientId) return [];
      const resp = await getAimKnowledgeRecencyTimeline(clientId);
      return resp?.data ?? [];
    },
    [clientId],
    Boolean(clientId),
  );
}

export function useAimAioFunnel(clientId, runId) {
  return useAsync(
    async () => {
      if (!clientId || !runId) return null;
      const resp = await getAimAioFunnel(clientId, runId);
      return resp?.data ?? null;
    },
    [clientId, runId],
    Boolean(clientId && runId),
  );
}

export function useAimOrganicRankingsDetail(clientId, responseId, enabled = true) {
  return useAsync(
    async () => {
      if (!clientId || !responseId) return [];
      const resp = await getAimOrganicRankingsDetail(clientId, { responseId });
      return resp?.data ?? [];
    },
    [clientId, responseId, enabled],
    Boolean(enabled && clientId && responseId),
  );
}

// Resolves the id of the run immediately preceding `runId` by created_at,
// so KPI comparisons have a sensible default when the user hasn't explicitly
// picked a compare run.
export function usePreviousRunId(clientId, runId) {
  const { data: runs } = useAimRuns(clientId, 20);
  return useMemo(() => {
    if (!runId || !Array.isArray(runs) || runs.length === 0) return '';
    const selected = runs.find((r) => r.id === runId);
    if (!selected) return '';
    const selectedAt = new Date(selected.created_at || 0).getTime();
    const prev = runs
      .filter((r) => r.id !== runId && new Date(r.created_at || 0).getTime() < selectedAt)
      .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())[0];
    return prev?.id || '';
  }, [runs, runId]);
}
